import { imageUrl, type RecipeDetail, type RecipeSummary } from '@dishly/shared';
import { api } from './api';
import { db, type CachedRecipe } from './db';

export function summaryOf(recipe: RecipeDetail): RecipeSummary {
  return {
    id: recipe.id,
    slug: recipe.slug,
    name: recipe.name,
    cover: recipe.cover,
    ratingAvg: recipe.ratingAvg,
    ratingCount: recipe.ratingCount,
    totalTimeMinutes: recipe.totalTimeMinutes,
    servings: recipe.servings,
    tags: recipe.tags,
    author: recipe.author,
    ingredients: recipe.ingredients,
    isDraft: recipe.isDraft,
    visibility: recipe.visibility,
    createdAt: recipe.createdAt,
    updatedAt: recipe.updatedAt,
    publishedAt: recipe.publishedAt,
  };
}

/** Stores a full recipe for offline reading and refreshes its summary. */
export async function cacheRecipe(recipe: RecipeDetail): Promise<CachedRecipe> {
  const cached: CachedRecipe = { ...recipe, cachedAt: Date.now() };
  await db.transaction('rw', db.recipes, db.summaries, async () => {
    await db.recipes.put(cached);
    await db.summaries.put(summaryOf(recipe));
  });
  return cached;
}

export async function fetchRecipe(id: string): Promise<CachedRecipe> {
  const cached = await cacheRecipe(await api<RecipeDetail>(`/recipes/${id}`));
  prefetchImages(cached);
  return cached;
}

/** Warms the service worker's image cache so the whole recipe works offline. */
function prefetchImages(recipe: RecipeDetail): void {
  if (!navigator.serviceWorker?.controller) return;
  const ids = [...recipe.images.map((i) => i.id), ...Object.keys(recipe.stepImages)];
  for (const id of ids) void fetch(imageUrl(id, 800)).catch(() => {});
}

export async function forgetRecipe(id: string): Promise<void> {
  await db.transaction('rw', db.recipes, db.summaries, db.progress, async () => {
    await Promise.all([db.recipes.delete(id), db.summaries.delete(id), db.progress.delete(id)]);
  });
}

export function recipePath(recipe: Pick<RecipeSummary, 'id' | 'slug'>): string {
  return `/recipes/${recipe.id}/${recipe.slug}`;
}
